import { SelectConfig } from './generic-select.component';
import { PROBATION_STATUS_OPTIONS } from '@shared/enums/probation-status.enum';
import { STUDY_PLAN_STATUS_OPTIONS, SKILL_OPTIONS } from '@shared/enums/study-plan.enum';
import { SESSION_STATUS_OPTIONS } from '@shared/enums/session-status.enum';
import { TICKET_TYPE_OPTIONS, TICKET_STATUS_OPTIONS } from '@shared/enums/ticket.enum';
import { SettingType } from '@shared/enums';

const SETTING_TYPE_OPTIONS = [
    { label: 'Course Level', value: SettingType.COURSE_LEVEL },
    { label: 'Test Result', value: SettingType.TEST_RESULT }
];

/**
 * Generic Select Configurations with Built-in Templates
 *
 * Each config can specify a templateName to use built-in templates:
 * - 'employee': Shows code chip + profile.fullname
 * - 'position': Shows code chip + name
 * - 'department': Shows code chip + name
 * - 'role': Shows code chip + name
 * - 'tracker': Shows employee.nickname + position.name + status + fromDate-toDate
 *
 * Usage:
 * <generic-p-select [config]="employeeSelectConfig"></generic-p-select>
 *
 * The template will be automatically applied based on config.templateName.
 * You can still override with custom ng-template if needed.
 */

// Employee Select Configuration - All employees
export const EMPLOYEE_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getEmployeeOptions',
    idField: 'id', // Field name to query by ID
    labelFormatter: (employee: any) => `${employee.code} - ${employee.fullname || 'N/A'} (${employee.nickname})`,
    valueField: 'id',
    placeholder: 'Chọn nhân viên',
    sortField: 'code,asc',
    templateName: 'employee',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim()

        if (idQuery && idQuery.id) {
            // When searching by ID (for pre-selected values)
            body.andQ = { id: idQuery.id };
        } else if (searchTerm && trimmedSearchTerm) {
            // When searching by text (user typing in filter)
            body.orQ = {
                code: trimmedSearchTerm,
                fullname: trimmedSearchTerm,
                nickname: trimmedSearchTerm,
            };
        }

        // Add any base query filters (always apply these)
        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            body.andQ = { ...body.andQ, ...baseQuery };
        }

        return body;
    }
};

// Employee Select Configuration - Filtered by department
export const EMPLOYEE_BY_DEPARTMENT_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getEmployeeOptions',
    idField: 'id', // Field name to query by ID
    labelFormatter: (employee: any) => `${employee.code} - ${employee.fullname || 'N/A'}`,
    valueField: 'id',
    placeholder: 'Chọn nhân viên trong phòng ban',
    sortField: 'code,asc',
    templateName: 'employee',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};

        if (idQuery && idQuery.id) {
            // When searching by ID (for pre-selected values)
            body.andQ = { id: idQuery.id };
        } else if (searchTerm && searchTerm.trim()) {
            // When searching by text (user typing in filter)
            body.orQ = {
                code: searchTerm.trim(),
                fullname: searchTerm.trim()
            };
        }

        // Add any base query filters (always apply these)
        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            body.andQ = { ...body.andQ, ...baseQuery };
        }

        return body;
    }
};

// Employee Select Configuration - Active employees only
export const ACTIVE_EMPLOYEE_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getEmployeeOptions',
    idField: 'id', // Field name to query by ID
    labelFormatter: (employee: any) => `${employee.code} - ${employee.fullname || 'N/A'}`,
    valueField: 'id',
    placeholder: 'Chọn nhân viên đang làm việc',
    sortField: 'code,asc',
    templateName: 'employee',
    baseQuery: { status: 'active' },
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};

        if (idQuery && idQuery.id) {
            // When searching by ID (for pre-selected values)
            body.andQ = { id: idQuery.id };
        } else if (searchTerm && searchTerm.trim()) {
            // When searching by text (user typing in filter)
            body.orQ = {
                code: searchTerm.trim(),
                fullname: searchTerm.trim()
            };
        }

        // Add any base query filters (always apply these)
        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            body.andQ = { ...body.andQ, ...baseQuery };
        }

        return body;
    }
};

// Department Select Configuration - Root departments only
export const ROOT_DEPARTMENT_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getRoots',
    idField: 'id', // Field name to query by ID
    labelFormatter: (dept: any) => `${dept.code} - ${dept.name}`,
    valueField: 'id',
    placeholder: 'Chọn phòng ban gốc',
    sortField: 'code,asc',
    templateName: 'department',
};

// Department Select Configuration - Child departments by parent
export const DEPARTMENT_BY_PARENT_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getChildren',
    idField: 'id', // Field name to query by ID
    labelFormatter: (dept: any) => `${dept.code} - ${dept.name}`,
    valueField: 'id',
    placeholder: 'Chọn team/subdept',
    sortField: 'code,asc',
    templateName: 'department',
    onDependencyChange: (parentId: any) => {
        return parentId ? { parentId: parentId } : {};
    }
};

// Course Select Configuration
export const COURSE_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getCourseOptions',
    idField: 'id', // Field name to query by ID
    labelFormatter: (course: any) => `${course.name} - ${course.level} (${course.type})`,
    valueField: 'id',
    placeholder: 'Chọn khóa học',
    sortField: 'name,asc',
    templateName: 'course',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim();

        if (idQuery) {
            body.andQ = idQuery;
        } else if (trimmedSearchTerm) {
            body.orQ = { name: trimmedSearchTerm };
        }

        if (baseQuery) {
            if (body.andQ) {
                body.andQ = { ...body.andQ, ...baseQuery };
            } else {
                body.andQ = baseQuery;
            }
        }

        return body;
    }
};

// Position Select Configuration
export const POSITION_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getPositionOptions',
    idField: 'id', // Field name to query by ID
    labelFormatter: (position: any) => `${position.code} - ${position.name}`,
    valueField: 'id',
    placeholder: 'Chọn vị trí',
    sortField: 'code,asc',
    templateName: 'position',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim();

        if (idQuery && idQuery.id) {
            // When searching by ID (for pre-selected values)
            body.andQ = { id: idQuery.id };
        } else if (searchTerm && trimmedSearchTerm) {
            // When searching by text (user typing in filter)
            body.orQ = {
                code: trimmedSearchTerm,
                name: trimmedSearchTerm
            };
        }

        // Add any base query filters (always apply these)
        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            body.andQ = { ...body.andQ, ...baseQuery };
        }

        return body;
    }
};

// Position Select Configuration - Filtered by department
export const POSITION_BY_DEPARTMENT_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getPositionOptions',
    idField: 'id', // Field name to query by ID
    labelFormatter: (position: any) => `${position.code} - ${position.name}`,
    valueField: 'id',
    placeholder: 'Chọn vị trí trong phòng ban',
    sortField: 'code,asc',
    templateName: 'position',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim();

        if (idQuery && idQuery.id) {
            // When searching by ID (for pre-selected values)
            body.andQ = { id: idQuery.id };
        } else if (searchTerm && trimmedSearchTerm) {
            // When searching by text (user typing in filter)
            body.orQ = {
                code: trimmedSearchTerm,
                name: trimmedSearchTerm
            };
        }

        // Add any base query filters (always apply these)
        // For department filtering, use rootDepartmentId as expected by backend
        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            // Map departmentId to rootDepartmentId for backend compatibility
            const mappedBaseQuery = { ...baseQuery };
            if (mappedBaseQuery.departmentId) {
                mappedBaseQuery.rootDepartmentId = mappedBaseQuery.departmentId;
                delete mappedBaseQuery.departmentId;
            }
            body.andQ = { ...body.andQ, ...mappedBaseQuery };
        }

        return body;
    }
};

// Role Select Configuration
export const ROLE_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getRoles',
    idField: 'id', // Field name to query by ID
    labelFormatter: (role: any) => role.name,
    valueField: 'id',
    placeholder: 'Chọn vai trò',
    sortField: 'name,1',
    templateName: 'role'
};

// Center Select Configuration
export const CENTER_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getOptions',
    idField: 'id', // Field name to query by ID
    labelFormatter: (center: any) => `${center.code} - ${center.name}`,
    valueField: 'id',
    placeholder: 'Chọn trung tâm',
    sortField: 'code,asc',
    templateName: 'center',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim();

        if (idQuery) {
            body.andQ = idQuery;
        } else if (trimmedSearchTerm) {
            body.orQ = {
                code: trimmedSearchTerm,
                name: trimmedSearchTerm
            };
        }

        if (baseQuery) {
            if (body.andQ) {
                body.andQ = { ...body.andQ, ...baseQuery };
            } else {
                body.andQ = baseQuery;
            }
        }

        return body;
    }
};

// Cluster Select Configuration
export const CLUSTER_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getOptions',
    idField: 'id', // Field name to query by ID
    labelFormatter: (cluster: any) => `${cluster.code} - ${cluster.name}`,
    valueField: 'id',
    placeholder: 'Chọn cụm',
    sortField: 'code,asc',
    templateName: 'cluster',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim();

        if (idQuery) {
            body.andQ = idQuery;
        } else if (trimmedSearchTerm) {
            body.orQ = {
                code: trimmedSearchTerm,
                name: trimmedSearchTerm
            };
        }

        if (baseQuery) {
            if (body.andQ) {
                body.andQ = { ...body.andQ, ...baseQuery };
            } else {
                body.andQ = baseQuery;
            }
        }

        return body;
    }
};

// Room Select Configuration
export const ROOM_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getOptions',
    idField: 'id', // Field name to query by ID
    labelFormatter: (room: any) => `${room.code.toUpperCase()}`,
    valueField: 'id',
    placeholder: 'Chọn phòng',
    sortField: 'code,asc',
    templateName: 'room', // Reuse department template (shows code + name)
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim();

        if (idQuery) {
            body.andQ = idQuery;
        } else if (trimmedSearchTerm) {
            body.orQ = {
                code: trimmedSearchTerm,
                name: trimmedSearchTerm
            };
        }

        if (baseQuery) {
            if (body.andQ) {
                body.andQ = { ...body.andQ, ...baseQuery };
            } else {
                body.andQ = baseQuery;
            }
        }

        return body;
    }
};

// Employee Position Select Configuration
export const EMPLOYEE_POSITION_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getOptions',
    idField: 'id', // Field name to query by ID
    labelFormatter: (employeePosition: any) => `${employeePosition.employee?.code || 'N/A'} - ${employeePosition.employee?.name || 'N/A'} (${employeePosition.position?.name || 'N/A'})`,
    valueField: 'id',
    placeholder: 'Chọn vị trí nhân viên',
    sortField: 'employee.code,asc',
    templateName: 'employeePosition',
    queryBuilder: (searchTerm: string, baseQuery: any) => {
        const query: any = { ...baseQuery };

        if (searchTerm && searchTerm.trim()) {
            query.text = searchTerm.trim();
        }

        return query;
    }
};

// Probation Status Select Configuration
export const PROBATION_STATUS_SELECT_CONFIG: SelectConfig = {
    staticOptions: PROBATION_STATUS_OPTIONS,
    optionLabel: 'label',
    optionValue: 'value',
    placeholder: 'Chọn trạng thái thử việc'
};

// Employee Position Select Configuration - Filtered by Employee ID
export const EMPLOYEE_POSITION_BY_EMPLOYEE_SELECT_CONFIG: SelectConfig = {
    service: null,
    method: 'getOptions',
    idField: 'id',
    valueField: 'id',
    labelFormatter: (employeePosition: any) => `${employeePosition.positionName || 'N/A'}`,
    sortField: 'positionName,asc',
    placeholder: 'Chọn vị trí',
    templateName: 'employeePosition',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery: any) => {
        const body: any = { ...baseQuery };

        if (idQuery) {
            body.andQ = { id: idQuery.id };
        } else if (searchTerm && searchTerm.trim()) {
            body.orQ = {
                positionName: searchTerm.trim()
            };
        }

        // Add any base query filters (always apply these)
        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            body.andQ = { ...body.andQ, ...baseQuery };
        }

        return body;
    },
    // Dependency callback only - dependsOn field removed as it's not used
    onDependencyChange: (employeeId: any) => {
        return employeeId ? { andQ: { employeeId: employeeId } } : {};
    }
};

// Study Plan Status Select Configuration
export const STUDY_PLAN_STATUS_SELECT_CONFIG: SelectConfig = {
    staticOptions: STUDY_PLAN_STATUS_OPTIONS,
    optionLabel: 'label',
    optionValue: 'value',
    placeholder: 'Chọn trạng thái',
    templateName: 'studyPlanStatus'
};

// Skills Multi-Select Configuration
export const SKILLS_SELECT_CONFIG: SelectConfig = {
    staticOptions: SKILL_OPTIONS,
    optionLabel: 'label',
    optionValue: 'value',
    placeholder: 'Chọn kỹ năng'
};

export const SESSION_STATUS_OPTIONS_SELECT_CONFIG: SelectConfig = {
    staticOptions: SESSION_STATUS_OPTIONS,
    optionLabel: 'label',
    optionValue: 'value',
    placeholder: 'Chọn trạng thái',
    templateName: 'sessionStatus'
};

// Study Plan Select Configuration
export const STUDY_PLAN_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getOptions',
    idField: 'id',
    labelFormatter: (studyPlan: any) => {
        const startDate = studyPlan.startDate ? new Date(studyPlan.startDate).toLocaleDateString('vi-VN') : 'N/A';
        const endDate = studyPlan.endDate ? new Date(studyPlan.endDate).toLocaleDateString('vi-VN') : 'N/A';
        return `${startDate} - ${endDate}`;
    },
    valueField: 'id',
    placeholder: 'Chọn kế hoạch học tập',
    sortField: 'startDate,desc',
    templateName: 'studyPlan',
    usePostMethod: true,
    bodyBuilder: (_searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};

        if (idQuery && idQuery.id) {
            body.andQ = { id: idQuery.id };
        }

        // Add any base query filters (always apply these)
        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            body.andQ = { ...body.andQ, ...baseQuery };
        }

        return body;
    },
    onDependencyChange: (learnerId: any) => {
        return learnerId ? { andQ: { learnerId: learnerId } } : {};
    }
};

// Learner Select Configuration
export const LEARNER_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getLearnerOptions',
    idField: 'id',
    labelFormatter: (learner: any) => `${learner.code} - ${learner.fullname}`,
    valueField: 'id',
    placeholder: 'Chọn học viên',
    sortField: 'code,asc',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim();

        if (idQuery && idQuery.id) {
            body.andQ = { id: idQuery.id };
        } else if (trimmedSearchTerm) {
            body.orQ = {
                code: trimmedSearchTerm,
                fullname: trimmedSearchTerm
            };
        }

        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            body.andQ = { ...body.andQ, ...baseQuery };
        }

        return body;
    }
};

// GV/TA Select Configuration - Filters employee positions with GV or TA positions
export const GV_TA_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getOptions',
    idField: 'id',
    labelFormatter: (empPos: any) => `${empPos.employee?.code || 'N/A'} - ${empPos.employee?.nickname || 'N/A'}`,
    valueField: 'id',
    placeholder: 'Chọn GV/TA',
    sortField: 'status,asc',
    templateName: 'employeePositionWithNames',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, _baseQuery: any, idQuery?: any) => {
        const body: any = {};

        if (idQuery) {
            body.andQ = { id: idQuery.id };
        } else if (searchTerm && searchTerm.trim()) {
            body.orQ = {
                employeeCode: searchTerm.trim(),
                employeeNickname: searchTerm.trim()
            };
        }

        // Filter for GV or TA positions only
        const positionFilter = {
            'position.code': ['gv', 'ta']
        };

        if (!body.andQ) {
            body.andQ = {};
        }
        body.andQ = { ...body.andQ, ...positionFilter };

        return body;
    }
};

// Tracker Select Configuration - Filters employee positions with isTracker=true
export const TRACKER_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getTrackerOptions',
    idField: 'id',
    labelFormatter: (tracker: any) => `${tracker.employee?.nickname || 'N/A'} - ${tracker.position?.name || 'N/A'} (${tracker.status || 'N/A'})`,
    valueField: 'id',
    placeholder: 'Chọn Tracker',
    sortField: 'status,asc',
    templateName: 'employeePositionWithNames',
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim();

        if (idQuery && idQuery.id) {
            body.andQ = { id: idQuery.id };
        } else if (trimmedSearchTerm) {
            body.orQ = { text: trimmedSearchTerm };
        }

        // Add any base query filters (always apply these)
        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            body.andQ = { ...body.andQ, ...baseQuery };
        }

        return body;
    }
};

// Setting Type Select Configuration
export const SETTING_TYPE_SELECT_CONFIG: SelectConfig = {
    staticOptions: SETTING_TYPE_OPTIONS,
    optionLabel: 'label',
    optionValue: 'value',
    placeholder: 'Chọn loại tag'
};

// Ticket Type Select Configuration
export const TICKET_TYPE_SELECT_CONFIG: SelectConfig = {
    staticOptions: TICKET_TYPE_OPTIONS,
    optionLabel: 'label',
    optionValue: 'value',
    placeholder: 'Chọn loại ticket',
    templateName: 'ticketType'
};

// Ticket Status Select Configuration
export const TICKET_STATUS_SELECT_CONFIG: SelectConfig = {
    staticOptions: TICKET_STATUS_OPTIONS,
    optionLabel: 'label',
    optionValue: 'value',
    placeholder: 'Chọn trạng thái',
    templateName: 'ticketStatus'
};

// Assignee (Employee) Select Configuration
export const ASSIGNEE_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getEmployeeOptions',
    idField: 'id',
    labelFormatter: (employee: any) => `${employee.code} - ${employee.fullname || 'N/A'} (${employee.nickname})`,
    valueField: 'id',
    placeholder: 'Chọn người phụ trách',
    sortField: 'code,asc',
    templateName: 'assignee',
    placeholderSubstitute: true,
    usePostMethod: true,
    bodyBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const body: any = {};
        const trimmedSearchTerm = searchTerm.trim();

        if (idQuery && idQuery.id) {
            body.andQ = { id: [idQuery.id] };
        } else if (trimmedSearchTerm) {
            body.orQ = {
                code: trimmedSearchTerm,
                fullname: trimmedSearchTerm,
                nickname: trimmedSearchTerm
            };
        }

        if (baseQuery && Object.keys(baseQuery).length > 0) {
            if (!body.andQ) {
                body.andQ = {};
            }
            body.andQ = { ...body.andQ, ...baseQuery };
        }

        return body;
    }
};


export const ZNS_TEMPLATE_SELECT_CONFIG: SelectConfig = {
    service: null, // Will be injected
    method: 'getListTemplate',
    idField: 'templateId', // Field name to query by ID
    labelFormatter: (template: any) => `${template.templateName} - ${template.templateId}`,
    valueField: 'templateId',
    placeholder: 'Chọn template',
    sortField: 'templateName,asc',
    templateName: 'znsTemplate',
    usePostMethod: false,
    queryBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {
        const query: any = { ...baseQuery };

        if (idQuery) {
            query.id = idQuery.id;
        } else if (searchTerm && searchTerm.trim()) {
            query.text = searchTerm.trim();
        }

        return query;
    },
    transformResponse: (response: any) => {
        
        if (response?.data?.data && Array.isArray(response.data.data)) {
            const transformed = {
                ...response,
                data: {
                    items: response.data.data.map((item: any) => ({
                        ...item,
                        id: item.templateId 
                    }))
                }
            };
            return transformed;
        }
        return response;
    }
};

export const ZNS_TEMPLATE_DETAIL_SELECT_CONFIG: SelectConfig = { 
    service: null, // Will be injected
    method: 'getTemplateDetail',
    idField: 'templateDetailId', // Field name to query by ID
    labelFormatter: (templateDetail: any) => `${templateDetail.templateDetailName} - ${templateDetail.templateDetailId}`,
    valueField: 'templateDetailId',
    placeholder: 'Chọn template detail',
    sortField: 'templateDetailName,asc',
    templateName: 'znsTemplateDetail',
    usePostMethod: false,
    queryBuilder: (searchTerm: string, baseQuery: any, idQuery?: any) => {  
        const query: any = { ...baseQuery };

        if (idQuery) {
            query.id = idQuery.id;
        } else if (searchTerm && searchTerm.trim()) {
            query.text = searchTerm.trim();
        }

        return query;
    },
    transformResponse: (response: any) => {
        // Transform response from { data: { data: [...] } } to { data: { items: [...] } }
        // so that after errorHandler extracts response.data, we get { items: [...] }
        if (response?.data?.data && Array.isArray(response.data.data)) {
            return {
                ...response,
                data: {
                    items: response.data.data
                }
            };
        }
        return response;
    }
};
